<template>
  <property-report-card
    title="Finishes"
    description="Compare finish levels across your comps to refine your positioning."
    :printing-mode="printingMode"
  >
    <q-table
      id="intercom-finishes-content"
      :rows="rows"
      :columns="columns"
      :rows-per-page-options="[0]"
      hide-bottom
      bordered
      dense
      flat
      :table-header-class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'"
    >
      <template #body="props">
        <q-tr
          :props="props"
          :class="{
            'text-bold bg-deep-purple-light text-primary':
              subjectDetail && props.row.id === subjectDetail?.id
          }"
        >
          <q-td v-for="(col, colIndex) in props.cols" :key="col.name" :props="props">
            <template v-if="colIndex > 0">
              <q-avatar
                rounded
                size="25px"
                :style="{
                  backgroundColor: similarityColor(col.value ? col.value / 10.0 : null),
                  color: similarityTextColor(col.value ? col.value / 10.0 : null),
                  fontWeight: 'medium'
                }"
              >
                <template v-if="col.value === null">-</template>
                <template v-else>{{ col.value }}</template>
              </q-avatar>
            </template>
            <template v-else>
              {{ col.value }}
            </template>
          </q-td>
        </q-tr>
        <q-tr
          v-if="props.rowIndex === rows.length - 1"
          class="text-bold text-center title-row"
          :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'"
        >
          <q-td class="text-left">
            <div>
              Comp Average
              <q-icon v-if="subjectDetail && !printingMode" name="info_outline">
                <q-tooltip>
                  Does not include
                  {{ subjectDetail.building_name || subjectDetail.street_address }}
                </q-tooltip>
              </q-icon>
            </div>
          </q-td>
          <q-td v-for="(val, i) in footerRow" :key="i">
            {{ val }}
          </q-td>
        </q-tr>
      </template>
    </q-table>
  </property-report-card>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import type { QTableColumn } from 'quasar'
import type {
  PropertyDetailsResponse,
  BuildingQuality,
  Availability,
  Price,
  AvailabilityPeriod
} from 'common'
import { similarityColor, similarityTextColor, unitsFound } from '@/utils'
import PropertyReportCard from '@/components/PropertyReportCard.vue'

export default {
  name: 'PropertyCompareFinishes',
  components: { PropertyReportCard },
  props: {
    filteredProperties: {
      type: Array<{
        prop: PropertyDetailsResponse
        units: {
          unit: Availability
          lastHistory: Price
          period: AvailabilityPeriod
        }[]
      }>,
      required: true
    },
    subjectDetail: {
      type: Object as PropType<PropertyDetailsResponse | null>,
      default: null
    },
    printingMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      similarityColor
    }
  },
  computed: {
    roomQuality() {
      type Key = keyof BuildingQuality
      const mutedRooms = [
        'avg_quality_score_walk_in_closet',
        'avg_quality_score_stairs_hallway',
        'avg_quality_score_main_entrance'
      ]
      const imageLabels = new Set<Key>()

      for (const { prop } of this.filteredProperties) {
        for (const key of Object.keys(prop.building_quality) as Key[]) {
          if (prop.building_quality[key] && !mutedRooms.includes(key)) {
            imageLabels.add(key)
          }
        }
      }
      return [...imageLabels]
    },
    footerRow() {
      if (this.columns) {
        return this.columns.slice(1).map((col) => {
          const vals = this.rows
            .filter((_) => _.id !== this.subjectDetail?.id)
            .map((row) => row[col.field as string])
            .filter((_) => _) as number[]
          if (!vals.length) {
            return null
          }
          return `${Math.round((vals.reduce((a, b) => a + b) / vals.length) * 10)}/10`
        })
      }
      return []
    },
    rows() {
      return this.filteredProperties.flatMap(({ prop }) => {
        return [
          this.roomQuality.reduce(
            (row: { [key: string]: number | string | null }, key) => {
              row[key] = prop.building_quality[key]
              return row
            },
            { id: prop.id, label: prop.building_name || prop.street_address }
          )
        ]
      })
    },
    columns() {
      return [
        { name: '', label: 'Finishes', field: 'label', align: 'left', classes: 'constrained-cell' },
        {
          name: 'overall',
          label: 'Overall',
          field: 'property_overall_quality',
          align: 'center',
          format: (_: number | null) => {
            if (_) {
              return `${Math.round(_ * 10)}`
            }
            return ''
          },
          sortable: true
        }
      ].concat(
        this.roomQuality
          .map((field: string) => ({
            name: this.toTitle(field),
            label: this.toTitle(field),
            format: (_: number | null) => {
              if (_) {
                return `${Math.round(_ * 10)}`
              }
              return ''
            },
            field,
            align: 'center',
            sortable: true
          }))
          .filter((_) => _.field !== 'property_overall_quality')
      ) as QTableColumn[]
    }
  },
  methods: {
    unitsFound,
    similarityTextColor,
    toTitle(label: string): string {
      return label
        .replace('avg_quality_score_', '')
        .replace('property_', '')
        .replace('quality', '')
        .split('_')
        .map((_) => _.slice(0, 1).toUpperCase() + _.slice(1))
        .join(' ')
    }
  }
}
</script>
<style scoped>
.title-row td {
  border-top: 2px solid rgba(0, 0, 0, 0.12);
}
.constrained-cell {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
